import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { ReferralModel } from 'shared/models/referral.model';
import { Referral, ReferralStatus, ReferralStatusCode } from 'app/authentication/signup/types';
import { OrganizationType } from '../authenticatedUser';
import { addDiffTime } from 'app/content/helpers/date';

export const referralSignupGuard: CanActivateFn = (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return combineLatest([authService.isAuthenticated$, authService.idTokenClaims$]).pipe(
        switchMap(([isAuthenticated, idToken]) => {
            if (!isAuthenticated) {
                return authService
                    .loginWithRedirect({
                        appState: {
                            target: state.url
                        },
                        authorizationParams: {
                            screen_hint: 'signup'
                        }
                    })
                    .pipe(map(() => false));
            }

            if (!idToken?.email_verified) {
                const referralId = getReferralIdFromRoute(route);
                const fallbackRoute = `/signup/referral/${referralId}/verify-email`;
                return of(router.parseUrl(fallbackRoute));
            }

            return of(true);
        })
    );
};

export const referralStatusGuard: CanActivateFn = (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const referralId = getReferralIdFromRoute(route);

    if (!referralId) {
        // link: placeholder for the inexistent referralId. Allow to redirect to the expired page.
        return router.parseUrl(`/signup/referral/link/status/${ReferralStatusCode.INVALID}`);
    }

    const referral$ = inject(ReferralModel).getReferral(referralId);

    return combineLatest([referral$, authService.idTokenClaims$]).pipe(
        map(([referral, idToken]) => {
            const url = state.url;

            const result = handleReferralRedirection({
                referral,
                isUsersEmailVerified: idToken?.email_verified,
                url
            });

            // Skip redirection if the user is already on the correct page
            // This is important to avoid infinite redirection loops
            if (result === url) {
                return true;
            }

            return typeof result === 'string' ? router.parseUrl(result) : result;
        }),
        catchError((error) => {
            console.error('referralStatusGuard - Error fetching referral:', error);
            return of(router.parseUrl(`/signup/referral/${referralId}/error`));
        })
    );
};

const handleReferralRedirection = ({
    referral,
    isUsersEmailVerified,
    url
}: {
    url: string;
    referral: Referral;
    isUsersEmailVerified: boolean;
}) => {
    if (referral?.status === ReferralStatus.SignupCompleted) {
        return `/signup/referral/${referral._id}/status/${ReferralStatusCode.EXPIRED}`;
    }

    if (_.isEmpty(referral) || !isValid(referral)) {
        return `/signup/referral/${referral._id}/status/${ReferralStatusCode.INVALID}`;
    }

    if (isUsersEmailVerified && referral?.status === ReferralStatus.Pending) {
        const queryParams = new URLSearchParams({
            step: '1',
            referralId: referral._id,
            organizationType: OrganizationType.Retailer,
            referralType: referral?.referralType
        }).toString();
        return `/signup/referral/join?${queryParams}`;
    }

    if (referral?.status === ReferralStatus.SignupStarted && !url.includes('finish')) {
        return `/signup/referral/${referral._id}/thank-you`;
    }

    if (isUsersEmailVerified === false) {
        return `/signup/referral/${referral._id}/verify-email`;
    }

    return true;
};

const isValid = (referral: Referral) => {
    const baseDate = referral.updated ?? referral.created;
    return (
        !referral.deleted &&
        referral.status !== ReferralStatus.SignupCompleted &&
        addDiffTime(new Date(baseDate), 'day', 60).getTime() > Date.now()
    );
};

const getReferralIdFromRoute = (route: ActivatedRouteSnapshot) =>
    route.params?.referralId || route.queryParams?.referralId;
